import { useMemo } from "react";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { useSwivuChecklistItems } from "./useSwivuChecklistItems";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { ChecklistItems } from "@/bryntum-lib/models/ChecklistItems";

/**
 * Consolidated hook for fetching and computing checklist items data
 * Eliminates duplication across ChecklistDetailView, ChecklistRow, and ChecklistAddItemsModal
 */
export const useChecklistItemsData = (checklistId: number | string) => {
  const { version } = useSwivuStore<ChecklistItems>({ storeId: STORE_KEYS.checklistItemStore });
  const { getChecklistItems } = useSwivuChecklistItems();

  // Get all items, filtered and sorted
  const items = useMemo(() => {
    const allItems = getChecklistItems(checklistId as number);
    return allItems.filter((item) => !item.archived).sort((a, b) => a.order - b.order);
  }, [checklistId, version, getChecklistItems]);

  // Compute completion stats
  const stats = useMemo(() => {
    const totalItems = items.length;
    const completedItems = items.filter((item) => item.isItemCheck).length;
    const completionPercentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

    return {
      totalItems,
      completedItems,
      completionPercentage
    };
  }, [items]);

  return {
    items,
    ...stats,
    version // Expose version in case consumers need it
  };
};
